import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, CreditCard, TrendingUp, PiggyBank, ShieldCheck, Wallet, IndianRupee, BadgeCheck, Lock, Zap } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

const CATEGORY_ICONS = [
  [/loan/i, Landmark],
  [/credit|card/i, CreditCard],
  [/demat|trading|stock/i, TrendingUp],
  [/mutual|fund|sip|invest/i, PiggyBank],
  [/insurance/i, ShieldCheck],
  [/account|bank|saving/i, Wallet],
];
const catIcon = (cat = "") => (CATEGORY_ICONS.find(([re]) => re.test(cat)) || [null, IndianRupee])[1];

export function AuthShell({ title, subtitle, children, footer }) {
  const [products, setProducts] = useState([]);
  const [earnMap, setEarnMap] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => setProducts((r.data || []).slice(0, 6))).catch(() => {});
    api.get("/campaigns/public").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        const seg = (c.segment || "").toLowerCase();
        if (!seg || c.payout_type === "percent") return;
        m[seg] = Math.max(m[seg] || 0, c.payout_pool || 0);
      });
      setEarnMap(m);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between gap-8 bg-brand-gradient text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #10B981 0, transparent 45%)" }} />
        <Logo height={44} light />
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight">India's direct Refer &amp; Earn fintech marketplace.</h2>
          <p className="mt-4 text-white/70 text-lg">Apply for financial products, share referral links, and earn transparent multi-level rewards.</p>
          {products.length > 0 && (
            <div className="mt-8" data-testid="auth-product-showcase">
              <p className="text-[11px] font-bold uppercase tracking-widest text-teal-300 mb-3">Popular products on Credit Kendra</p>
              <div className="grid grid-cols-2 gap-3">
                {products.map((p) => {
                  const Icon = catIcon(p.category);
                  const earn = earnMap[(p.category || "").toLowerCase()] || 0;
                  return (
                    <div key={p.id} data-testid={`auth-product-${p.id}`} className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm p-3.5">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 shrink-0 rounded-lg bg-teal-400/20 flex items-center justify-center"><Icon className="w-4 h-4 text-teal-300" /></span>
                        <p className="text-sm font-bold leading-tight">{p.name}</p>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-[11px] text-white/60">{p.category}</p>
                        {earn > 0 && <p className="text-[11px] font-bold text-teal-300">Earn up to ₹{earn.toLocaleString("en-IN")}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="relative space-y-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/70" data-testid="auth-trust-points">
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-teal-300" /> 100% Digital KYC</span>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-teal-300" /> Secure &amp; transparent payouts</span>
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-teal-300" /> Multi-level rewards</span>
          </div>
          <p className="text-xs text-white/50">© {new Date().getFullYear()} Credit Kendra</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo height={40} /></div>
          <h1 className="font-display text-3xl font-extrabold text-[#0B223D]">{title}</h1>
          <p className="text-slate-500 mt-2">{subtitle}</p>
          {products.length > 0 && (
            <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar mt-5 -mx-1 px-1" data-testid="auth-product-chips-mobile">
              {products.map((p) => {
                const Icon = catIcon(p.category);
                return (
                  <span key={p.id} className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#0B223D] flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-teal-600" /> {p.name}
                  </span>
                );
              })}
            </div>
          )}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
